import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Users, Calendar, Clock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Courses = () => {
  const [enrolling, setEnrolling] = useState(false);
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [enrollData, setEnrollData] = useState({ name: "", email: "" });
  const [error, setError] = useState(null);

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!enrollData.name || !enrollData.email) {
      setError("Please fill in all fields");
      return;
    }
    
    setEnrolling(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/courses/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: enrollData.name,
          email: enrollData.email,
          course_id: "nervous-system-foundations",
          origin_url: window.location.origin
        })
      });
      
      if (!response.ok) throw new Error("Failed to create checkout");
      
      const data = await response.json();
      window.location.href = data.url;
    } catch (e) {
      setError("Something went wrong. Please try again.");
      setEnrolling(false);
    }
  };

  const weeks = [
    {
      number: 1,
      title: "Breath Mechanics & Awareness",
      goal: "Understand how you currently breathe",
      topics: [
        "Upper chest vs diaphragm breathing",
        "Pressure awareness in the body",
        "Basic downshift protocol (3-5 min reset)",
        "Postural breath mapping"
      ],
      outcome: "You can feel when you are dysregulated."
    },
    {
      number: 2,
      title: "Nervous System Regulation",
      goal: "Change your state on purpose",
      topics: [
        "Recognising your stress signatures",
        "Hyper / hypo arousal patterns",
        "Controlled down-regulation techniques",
        "Micro resets during the day"
      ],
      outcome: "You experience a successful state shift. This is where sovereignty starts."
    },
    {
      number: 3,
      title: "Structural Coherence & Presence",
      goal: "Stability under mild stress",
      topics: [
        "Posture + breath integration",
        "Eye contact + exhale control",
        "Voice tone + regulation",
        "Staying steady during minor triggers"
      ],
      outcome: "You feel different in conversations."
    },
    {
      number: 4,
      title: "Relational Stability & Integration",
      goal: "Response over reaction",
      topics: [
        "Trigger buffering techniques",
        "Boundaries without escalation",
        "The 'Pause Protocol'",
        "Building your personal daily regulation plan"
      ],
      outcome: "Your internal steadiness baseline increases."
    }
  ];

  const outcomes = [
    {
      title: "Functional Breath Competence",
      description: "Not 'understanding breathing' — but the ability to identify tension patterns, downshift in 3-5 minutes, and know exactly what to do when stress rises."
    },
    {
      title: "Pattern Recognition Before Escalation",
      description: "You'll identify your early stress signals, name your stress signature, pause before reaction, and choose a different path. Nervous system literacy."
    },
    {
      title: "Applied Choice Under Pressure",
      description: "Slow down your breathing during conflict, hold posture and eye contact, respond instead of react. Choice becomes physiological control."
    }
  ];

  return (
    <div className="min-h-screen bg-ink" data-testid="courses-page">
      {/* Hero */}
      <section className="pt-32 pb-20 relative">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">
            4-Week Live Program
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#EAEAEA] mb-6">
            Nervous System Foundations
          </h1>
          <p className="text-[#A1A1AA] text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-8">
            Learn to regulate your nervous system, recognise your patterns, and respond instead of react.
          </p>
          
          <div className="flex flex-wrap justify-center gap-6 text-[#A1A1AA]">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-gold" />
              <span>Starts Wednesday, April 8th</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-gold" />
              <span>6:00 - 7:30pm</span>
            </div>
            <div className="flex items-center gap-2">
              <Users size={18} className="text-gold" />
              <span>Limited to 12 people</span>
            </div>
          </div>
        </div>
      </section>

      {/* Price Banner */}
      <section className="py-8 bg-gold/10 border-y border-gold/20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            <div>
              <span className="text-gold text-xs uppercase tracking-widest">Beta Cohort Price</span>
              <div className="font-heading text-4xl text-[#EAEAEA]">$349 <span className="text-lg text-[#A1A1AA]">AUD</span></div>
            </div>
            <div className="text-[#A1A1AA] text-sm max-w-md text-left">
              This is a founding cohort rate. Future programs will be priced higher. 
              Secure your place at this investment level.
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-2xl text-[#EAEAEA] mb-8 text-center">This program is for you if...</h2>
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {[
              "You feel hijacked by your emotions",
              "You react before you can think",
              "Stress lives in your body",
              "You want practical tools, not theory",
              "You're ready to build a daily practice",
              "You want to feel steady in difficult moments"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 text-[#A1A1AA]">
                <Check size={18} className="text-gold mt-1 flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Week by Week */}
      <section className="py-20 bg-paper">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-4 text-center">The 4-Week Journey</h2>
          <p className="text-[#A1A1AA] text-center mb-12 max-w-2xl mx-auto">
            Each week builds on the last. By the end, you'll have a complete toolkit for nervous system regulation.
          </p>
          
          <div className="space-y-8">
            {weeks.map((week) => (
              <div key={week.number} className="p-8 border border-white/5 rounded-sm hover:border-gold/20 transition-colors">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                      <span className="font-heading text-2xl text-gold">{week.number}</span>
                    </div>
                  </div>
                  
                  <div className="flex-grow">
                    <h3 className="font-heading text-xl text-[#EAEAEA] mb-1">{week.title}</h3>
                    <p className="text-gold text-sm mb-4">Goal: {week.goal}</p>
                    
                    <div className="grid md:grid-cols-2 gap-2 mb-4">
                      {week.topics.map((topic, i) => (
                        <div key={i} className="flex items-start gap-2 text-[#A1A1AA] text-sm">
                          <span className="text-gold">•</span>
                          <span>{topic}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="pt-4 border-t border-white/5">
                      <p className="text-[#666] text-xs uppercase tracking-widest mb-1">Outcome</p>
                      <p className="text-[#EAEAEA] text-sm italic">{week.outcome}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Leave With */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-4 text-center">What you'll actually leave with</h2>
          <p className="text-[#A1A1AA] text-center mb-12 max-w-2xl mx-auto">
            Not concepts. Competence.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {outcomes.map((outcome, i) => (
              <div key={i} className="p-6 border border-white/5 rounded-sm">
                <div className="w-10 h-10 rounded-sm bg-gold/10 border border-gold/30 flex items-center justify-center mb-4">
                  <span className="font-heading text-lg text-gold">{i + 1}</span>
                </div>
                <h3 className="font-heading text-lg text-[#EAEAEA] mb-3">{outcome.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{outcome.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enroll Section */}
      <section className="py-20 bg-paper">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-4 text-center">Secure your place</h2>
          <p className="text-[#A1A1AA] text-center mb-10">
            First cohort begins April 8th. Limited to 12 participants.
          </p>
          
          <div className="text-center space-y-6">
            {!showEnrollForm ? (
              <button
                onClick={() => setShowEnrollForm(true)}
                className="inline-block bg-gold text-void font-body font-semibold uppercase tracking-widest py-4 px-12 rounded-sm hover:bg-gold/90 transition-colors text-center"
                data-testid="course-enroll-btn"
              >
                Enroll Now - $349 AUD
              </button>
            ) : (
              <form onSubmit={handleEnroll} className="max-w-md mx-auto space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={enrollData.name}
                    onChange={(e) => setEnrollData({...enrollData, name: e.target.value})}
                    className="w-full p-4 bg-ink border border-white/10 text-[#EAEAEA] placeholder-[#666] focus:border-gold/50 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Your email"
                    value={enrollData.email}
                    onChange={(e) => setEnrollData({...enrollData, email: e.target.value})}
                    className="w-full p-4 bg-ink border border-white/10 text-[#EAEAEA] placeholder-[#666] focus:border-gold/50 focus:outline-none"
                    required
                  />
                </div>
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={enrolling}
                  className="w-full bg-gold text-void font-body font-semibold uppercase tracking-widest py-4 px-12 rounded-sm hover:bg-gold/90 transition-colors disabled:opacity-50"
                >
                  {enrolling ? "Redirecting to checkout..." : "Proceed to Payment"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEnrollForm(false)}
                  className="text-[#666] text-sm hover:text-[#A1A1AA]"
                >
                  Cancel
                </button>
              </form>
            )}
            
            <p className="text-[#666] text-sm">
              Only 12 spots available. Secure checkout via Stripe.
            </p>
            
            <div className="pt-8 border-t border-white/10 mt-8">
              <p className="text-[#A1A1AA] text-sm mb-4">Questions before enrolling?</p>
              <Link 
                to="/sessions" 
                className="text-gold hover:text-gold/80 transition-colors text-sm"
              >
                Book a free 15-minute call →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Prefer 1:1 */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-[#A1A1AA] mb-4">Prefer private sessions?</p>
          <Link 
            to="/sessions" 
            className="inline-flex items-center gap-2 text-gold hover:text-gold/80 transition-colors group"
          >
            <span className="text-sm uppercase tracking-widest">Explore 1:1 Recalibration</span>
            <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Courses;
